// components/branches/config/appointment/BlockedPeriodsSettings.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  BanIcon,
  CheckIcon,
  XIcon,
} from "@heroicons/react/outline";
import {
  getBlockedPeriodsThunk,
  addBlockedPeriodThunk,
  updateBlockedPeriodThunk,
  deleteBlockedPeriodThunk,
  toggleBlockedPeriodThunk,
} from "../../../../store/thunks/branchAppointmentConfigThunks";
import NumberInput from "../../../common/NumberInput";

const BlockedPeriodsSettings = ({ branchCode, isEditMode }) => {
  const dispatch = useDispatch();
  const { blockedPeriods, loadingBlockedPeriods, successMessage } = useSelector(
    (state) => state.branchAppointmentConfig
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    reason: "CUSTOM",
    customReason: "",
    daysOfWeek: [],
    startTime: "09:00",
    endTime: "17:00",
    recurring: true,
    isActive: true,
    priority: 5,
    allowEmergency: false,
    specificDate: "",
  });

  const BLOCKED_PERIOD_REASONS = [
    { value: "STAFF_MEETING", label: "Staff Meeting" },
    { value: "LUNCH_BREAK", label: "Lunch Break" },
    { value: "INVENTORY_CHECK", label: "Inventory Check" },
    { value: "CLEANING", label: "Cleaning" },
    { value: "TRAINING", label: "Training" },
    { value: "SYSTEM_MAINTENANCE", label: "System Maintenance" },
    { value: "BUSY_PERIOD", label: "Busy Period" },
    { value: "CUSTOM", label: "Custom" },
  ];

  const DAYS_OF_WEEK = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  useEffect(() => {
    if (branchCode) {
      dispatch(getBlockedPeriodsThunk(branchCode));
    }
  }, [dispatch, branchCode]);

  useEffect(() => {
    if (successMessage) {
      setShowAddModal(false);
      setEditingIndex(null);
      resetForm();
    }
  }, [successMessage]);

  const resetForm = () => {
    setFormData({
      name: "",
      reason: "CUSTOM",
      customReason: "",
      daysOfWeek: [],
      startTime: "09:00",
      endTime: "17:00",
      recurring: true,
      isActive: true,
      priority: 5,
      allowEmergency: false,
      specificDate: "",
    });
  };

  const handleAddBlockedPeriod = () => {
    if (!formData.name || formData.daysOfWeek.length === 0) {
      alert("Please provide name and select at least one day");
      return;
    }

    dispatch(
      addBlockedPeriodThunk({
        branchCode,
        blockedPeriod: formData,
      })
    );
  };

  const handleUpdateBlockedPeriod = () => {
    if (editingIndex === null) return;

    dispatch(
      updateBlockedPeriodThunk({
        branchCode,
        blockIndex: editingIndex,
        updates: formData,
      })
    );
  };

  const handleDeleteBlockedPeriod = (index) => {
    if (
      window.confirm("Are you sure you want to delete this blocked period?")
    ) {
      dispatch(
        deleteBlockedPeriodThunk({
          branchCode,
          blockIndex: index,
        })
      );
    }
  };

  const handleToggleBlockedPeriod = (index) => {
    dispatch(
      toggleBlockedPeriodThunk({
        branchCode,
        blockIndex: index,
      })
    );
  };

  const handleEditStart = (period, index) => {
    setFormData(period);
    setEditingIndex(index);
    setShowAddModal(true);
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
  };

  const getReasonLabel = (reason) => {
    const found = BLOCKED_PERIOD_REASONS.find((r) => r.value === reason);
    return found ? found.label : reason;
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 8)
      return { color: "bg-red-100 text-red-800", label: "High" };
    if (priority >= 4)
      return { color: "bg-yellow-100 text-yellow-800", label: "Medium" };
    return { color: "bg-green-100 text-green-800", label: "Low" };
  };

  if (loadingBlockedPeriods) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BanIcon className="h-5 w-5 text-red-600" />
            Blocked Periods
          </h3>
          <p className="text-gray-600 mt-1">
            Configure time blocks when appointments cannot be scheduled
          </p>
        </div>
        {isEditMode && (
          <button
            onClick={() => {
              resetForm();
              setEditingIndex(null);
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Add Blocked Period
          </button>
        )}
      </div>

      {/* Blocked Periods List */}
      <div className="space-y-3">
        {blockedPeriods.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <BanIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No blocked periods configured</p>
            {isEditMode && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 text-blue-600 hover:text-blue-700"
              >
                Add your first blocked period
              </button>
            )}
          </div>
        ) : (
          blockedPeriods.map((period, index) => {
            const priorityBadge = getPriorityBadge(period.priority || 5);
            return (
              <div
                key={index}
                className={`border-2 rounded-lg p-4 transition-all ${
                  period.isActive
                    ? "border-gray-200 bg-white"
                    : "border-gray-300 bg-gray-50 opacity-60"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-lg">{period.name}</h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${priorityBadge.color}`}
                      >
                        {priorityBadge.label} Priority
                      </span>
                      {!period.isActive && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                          Inactive
                        </span>
                      )}
                      {period.allowEmergency && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Emergency OK
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Time:</span>
                        <span className="font-medium ml-2">
                          {period.startTime} - {period.endTime}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Reason:</span>
                        <span className="font-medium ml-2">
                          {getReasonLabel(period.reason)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Type:</span>
                        <span className="font-medium ml-2">
                          {period.recurring ? "Recurring" : "One-time"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Days:</span>
                        <span className="font-medium ml-2">
                          {period.daysOfWeek.length} day(s)
                        </span>
                      </div>
                    </div>

                    {/* Days badges */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {period.daysOfWeek.map((day) => (
                        <span
                          key={day}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                        >
                          {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        </span>
                      ))}
                    </div>

                    {period.customReason && (
                      <p className="text-sm text-gray-600 mt-2 italic">
                        {period.customReason}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {isEditMode && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleToggleBlockedPeriod(index)}
                        className={`p-2 rounded-lg transition-colors ${
                          period.isActive
                            ? "bg-gray-100 hover:bg-gray-200 text-gray-600"
                            : "bg-green-100 hover:bg-green-200 text-green-600"
                        }`}
                        title={period.isActive ? "Deactivate" : "Activate"}
                      >
                        {period.isActive ? (
                          <XIcon className="h-5 w-5" />
                        ) : (
                          <CheckIcon className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditStart(period, index)}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteBlockedPeriod(index)}
                        className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">
                {editingIndex !== null
                  ? "Edit Blocked Period"
                  : "Add Blocked Period"}
              </h3>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., Lunch Break"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <select
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {BLOCKED_PERIOD_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Reason */}
                {formData.reason === "CUSTOM" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Reason
                    </label>
                    <input
                      type="text"
                      value={formData.customReason}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customReason: e.target.value,
                        })
                      }
                      placeholder="Describe the reason"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) =>
                        setFormData({ ...formData, startTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time *
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) =>
                        setFormData({ ...formData, endTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Days of Week */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Days of Week *
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => handleDayToggle(day.value)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          formData.daysOfWeek.includes(day.value)
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {day.label.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (1-10)
                  </label>
                  <NumberInput
                    value={formData.priority}
                    onChange={(value) =>
                      setFormData({ ...formData, priority: value })
                    }
                    min="1"
                    max="10"
                    className="w-24 focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Higher priority blocks override lower priority ones
                  </p>
                </div>

                {/* Checkboxes */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="recurring"
                      checked={formData.recurring}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          recurring: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="recurring" className="text-sm">
                      Recurring (repeats weekly)
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="allowEmergency"
                      checked={formData.allowEmergency}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowEmergency: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="allowEmergency" className="text-sm">
                      Allow emergency appointments (with approval)
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="text-sm">
                      Active
                    </label>
                  </div>
                </div>

                {/* Specific Date (if not recurring) */}
                {!formData.recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specific Date
                    </label>
                    <input
                      type="date"
                      value={formData.specificDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          specificDate: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingIndex(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    editingIndex !== null
                      ? handleUpdateBlockedPeriod
                      : handleAddBlockedPeriod
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingIndex !== null ? "Update" : "Add"} Blocked Period
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-700">
              <strong>{blockedPeriods.length}</strong> blocked period(s)
              configured
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {blockedPeriods.filter((p) => p.isActive).length} active
            </p>
          </div>
          {blockedPeriods.some((p) => p.allowEmergency) && (
            <p className="text-sm text-blue-700">
              <strong>
                {blockedPeriods.filter((p) => p.allowEmergency).length}
              </strong>{" "}
              allow emergency bookings
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlockedPeriodsSettings;
